
import React from 'react';
import { format } from 'date-fns';
import { Mail, Phone } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { BookingDetails } from '@/hooks/bookings/types';
import { BookingStatusBadge } from '../utils/booking-status';
import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { Badge } from '@/components/ui/badge';
import { useUserRoles } from '@/hooks/useUserRoles';

interface BookingHeaderSectionProps {
  booking: BookingDetails;
  isAdmin: boolean;
  showContactButtons?: boolean;
}

const BookingHeaderSection: React.FC<BookingHeaderSectionProps> = ({
  booking,
  isAdmin,
  showContactButtons = true
}) => {
  const { roles } = useUserRoles();
  
  // Fetch organization data for super admins
  const { data: organization } = useQuery({
    queryKey: ['organization', booking.org_id],
    queryFn: async () => {
      if (!booking.org_id) return null;
      const { data, error } = await supabase
        .from('organizations')
        .select('id, name, display_name')
        .eq('id', booking.org_id)
        .single();
      
      if (error) throw error;
      return data;
    },
    enabled: !!booking.org_id && roles?.isPlatformAdmin
  });

  const formatDateTime = (dateString: string) => {
    try {
      const date = new Date(dateString);
      const time = format(date, 'HH:mm');
      const dateFormatted = format(date, 'dd MMM yyyy');
      return `${time} on ${dateFormatted}`;
    } catch {
      return 'Invalid date';
    }
  };

  const handlePhoneClick = (phoneNumber: string) => {
    window.open(`tel:${phoneNumber}`, '_self');
  };

  const handleEmailClick = (email: string) => {
    window.open(`mailto:${email}`, '_self');
  };

  const getBookerInfo = () => {
    if (booking.profiles?.name) {
      return booking.profiles.name;
    }
    if (booking.profiles?.phone_number) {
      return booking.profiles.phone_number;
    }
    if (booking.profiles?.email) {
      return booking.profiles.email;
    }
    return booking.user_id?.substring(0, 8) || 'Unknown';
  };

  return (
    <div className="space-y-3 mb-6 pb-4 border-b border-border/50">
      {/* Top row: Booked by info and status - stacks on mobile */}
      <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-3">
        <div className="flex flex-col gap-2 min-w-0 flex-1">
          <div className="flex flex-wrap items-center gap-2">
            <span className="text-base font-medium text-foreground/90">
              Booked by
            </span>
            <span className="text-base font-semibold text-primary break-all">
              {getBookerInfo()}
            </span>
          </div>
          {roles?.isPlatformAdmin && organization && (
            <Badge variant="outline" className="text-xs w-fit">
              {organization.display_name || organization.name}
            </Badge>
          )}
        </div>
        <div className="shrink-0 self-start">
          <BookingStatusBadge status={booking.status} />
        </div>
      </div>

      {/* Bottom row: ID and creation date - wraps on mobile */}
      <div className="flex flex-col sm:flex-row sm:items-center gap-2 sm:gap-3 text-sm text-muted-foreground/80">
        <span className="font-mono text-xs bg-muted/50 px-2 py-1 rounded w-fit break-all">
          #{booking.id}
        </span>
        <span className="text-xs sm:text-sm">Created: {formatDateTime(booking.created_at)}</span>
      </div>
      
      {isAdmin && booking.profiles && showContactButtons && (
        <div className="flex items-center gap-2 flex-wrap">
          {booking.profiles.phone_number && (
            <Button 
              variant="outline" 
              size="sm" 
              onClick={() => handlePhoneClick(booking.profiles!.phone_number!)} 
              className="h-8 px-3"
            >
              <Phone className="h-3 w-3 mr-1" />
              {booking.profiles.phone_number}
            </Button>
          )}
          {booking.profiles.email && (
            <Button 
              variant="outline" 
              size="sm" 
              onClick={() => handleEmailClick(booking.profiles!.email!)} 
              className="h-8 px-3"
            >
              <Mail className="h-3 w-3 mr-1" />
              {booking.profiles.email}
            </Button>
          )}
        </div>
      )}
    </div>
  );
};

export default BookingHeaderSection;
